// ConfettiCustom - Main Component
import React, { useRef, useEffect, useState } from 'react';
import { ConfettiCustomProps, ConfettiPiece } from './types';
import { ConfettiPhysics } from './physics';
import { ConfettiRenderer } from './renderer';
import { DEFAULT_PROPS, PHYSICS_CONFIG } from './constants';

const ConfettiCustom: React.FC<ConfettiCustomProps> = ({
  width,
  height,
  numberOfPieces = DEFAULT_PROPS.numberOfPieces,
  colors = DEFAULT_PROPS.colors,
  recycle = DEFAULT_PROPS.recycle,
  duration = DEFAULT_PROPS.duration,
  gravity = DEFAULT_PROPS.gravity,
  wind = DEFAULT_PROPS.wind,
  friction = DEFAULT_PROPS.friction,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const physicsRef = useRef<ConfettiPhysics>();
  const rendererRef = useRef<ConfettiRenderer>();
  const piecesRef = useRef<ConfettiPiece[]>([]);
  const [isAnimating, setIsAnimating] = useState(true);

  // Inicializar sistemas
  useEffect(() => {
    if (!canvasRef.current) return;

    // Configurar física
    physicsRef.current = new ConfettiPhysics({
      gravity,
      wind,
      friction
    });

    // Configurar renderer
    rendererRef.current = new ConfettiRenderer(canvasRef.current, {
      width,
      height,
      colors
    });

    // Gerar partículas iniciais
    piecesRef.current = [];
    for (let i = 0; i < numberOfPieces; i++) {
      piecesRef.current.push(
        physicsRef.current.generatePiece(width, height, colors)
      );
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, numberOfPieces, colors, gravity, wind, friction]);

  // Loop de animação
  useEffect(() => {
    if (!isAnimating || !physicsRef.current || !rendererRef.current) return;

    const animate = () => {
      const physics = physicsRef.current!;
      const renderer = rendererRef.current!;
      const pieces = piecesRef.current;

      // Limpar canvas
      renderer.clear();

      // Atualizar e renderizar cada partícula
      for (let i = pieces.length - 1; i >= 0; i--) {
        const piece = pieces[i];
        
        // Atualizar física
        physics.updatePiece(piece);
        
        // Renderizar partícula
        renderer.renderPiece(piece);
        
        // Remover partículas que saíram dos limites
        if (physics.isOutOfBounds(piece, width, height)) {
          if (recycle) {
            // Reciclar partícula
            pieces[i] = physics.generatePiece(width, height, colors);
          } else {
            // Remover partícula
            pieces.splice(i, 1);
          }
        }
      }

      // Continuar animação se ainda há partículas ou se está reciclando
      if (pieces.length > 0 || recycle) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating, width, height, colors, recycle]);

  // Parar animação após a duração especificada
  useEffect(() => {
    if (!recycle) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, recycle]);

  // Atualizar dimensões quando mudarem
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.updateDimensions(width, height);
    }
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    />
  );
};

export default ConfettiCustom;
